import type { DispensingRx } from '@/features/admin-v2/api';

/** RL-1630: the medicines need an ID check at hand-over (controlled or psychotropic). */
export const requiresIdCheck = (rx: DispensingRx) =>
  rx.requiresIdCheck ?? rx.needsIdCheck;

/** RL-1630: a high-alert medicine needs a second checker. */
export const requiresSecondCheck = (rx: DispensingRx) =>
  rx.requiresSecondCheck ?? rx.needsSecondCheck;

/**
 * RL-1629: the item a scanned barcode belongs to: `<medicine code>|<batch>`
 * names its medicine. A bare batch number names none (null: use the item
 * the dispenser chose).
 */
export function itemForCode(rx: DispensingRx, code: string): string | null {
  const bar = code.indexOf('|');
  if (bar < 1) return null;
  const medicine = code.slice(0, bar).trim();
  const matches = rx.items.filter((i) => i.catalogue?.code === medicine);
  return (matches.find((i) => !i.scanned) ?? matches[0])?.id ?? null;
}

/**
 * RL-1632: counselling is required when a medicine has counselling points
 * (the API's rule for "Dispensed").
 */
export const requiresCounselling = (rx: DispensingRx) =>
  rx.items.some((item) => (item.catalogue?.counselling?.length ?? 0) > 0);

/** Last 4 letters or digits of the ID seen (the API's rule). */
export const ID_LAST4 = /^[0-9A-Za-z]{4}$/;

/**
 * The checks the API still reports as missing, as i18n keys under
 * `dispensary.missing.*`, in the order the dispenser does them.
 */
export function missingChecks(rx: DispensingRx): string[] {
  const missing: string[] = [];
  if (!rx.items.length || rx.items.some((item) => item.scanned === null))
    missing.push('scan');
  // RL-1633: an older API does not send the tick; only `false` is missing.
  if (rx.clinicalVerified === false) missing.push('clinical');
  if (requiresCounselling(rx) && !rx.counsellingDone)
    missing.push('counselling');
  if (rx.needsIdCheck) missing.push('idCheck');
  if (rx.needsSecondCheck) missing.push('secondCheck');
  return missing;
}

export function safetyChecksComplete(rx: DispensingRx) {
  return missingChecks(rx).length === 0;
}
